/**
 * step.guard.ts
 * -------------
 * Route guard that prevents users from navigating to a step
 * before they have completed all prerequisite steps.
 *
 * Also redirects to the landing page if no flow mode has been chosen yet.
 */

import { inject }            from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SessionService, StepKey } from '../services/session.service';

/**
 * Map of each step to the step that must be completed before it.
 * If a step has no prerequisite it is always accessible once a flow is chosen.
 */
const PREREQUISITES: Partial<Record<StepKey, StepKey>> = {
  'category-review':    'document-upload',
  'assessment-report':  'category-review',   // new flow
  // For existing flow, assessment-report requires application-details
};

/**
 * Factory function — pass the step key this guard is protecting.
 *
 * Usage in routes:
 *   canActivate: [stepGuard('category-review')]
 */
export function stepGuard(step: StepKey): CanActivateFn {
  return () => {
    const session = inject(SessionService);
    const router  = inject(Router);
    const mode    = session.flowMode();

    // No flow chosen yet — send back to landing
    if (!mode) {
      router.navigate(['/']);
      return false;
    }

    // Special rule: existing flow cannot access new-flow-only steps
    if (mode === 'existing' && (step === 'document-upload' || step === 'category-review')) {
      router.navigate(['/application-details']);
      return false;
    }

    // For existing flow, assessment-report requires application-details
    if (mode === 'existing' && step === 'assessment-report') {
      if (!session.isStepComplete('application-details')) {
        router.navigate(['/application-details']);
        return false;
      }
      return true;
    }

    // Check generic prerequisite for new flow
    const prereq = PREREQUISITES[step];
    if (prereq && !session.isStepComplete(prereq)) {
      router.navigate(['/', prereq]);
      return false;
    }

    return true;
  };
}
